/**
 * EvidenceChip — the small pill that surfaces one piece of evidence next to a
 * recommendation, negative-keyword suggestion, or campaign-review row.
 *
 * G-8 requires every actionable row to carry the evidence that got it flagged.
 * This is the primitive every M-02..M-08 tab shares so operators see a
 * consistent shape across the seven tabs (bus factor).
 *
 * Tone options loosely map to what the chip means at a glance:
 * - neutral  — a factual descriptor (a campaign name, a match type)
 * - positive — evidence that argues FOR the row's action
 * - warning  — a soft flag that needs an operator's eye
 * - danger   — evidence of a real problem
 *
 * Every chip has one label. When the chip needs more context, its `title`
 * carries the full sentence so hover shows the full evidence without
 * cluttering the row.
 */
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type EvidenceTone = 'neutral' | 'positive' | 'warning' | 'danger';

interface EvidenceChipProps {
  label: ReactNode;
  tone?: EvidenceTone;
  title?: string;
  className?: string;
}

const TONE_CLASSES: Record<EvidenceTone, string> = {
  neutral: 'border-border bg-muted/40 text-muted-foreground',
  positive: 'border-teal/30 bg-teal-light text-teal-dark',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  danger: 'border-coral/40 bg-coral-light text-coral-dark',
};

export function EvidenceChip({
  label,
  tone = 'neutral',
  title,
  className,
}: EvidenceChipProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
        'text-[11px] font-medium leading-4',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}

export default EvidenceChip;
